import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col, Card, Badge } from 'react-bootstrap';
import { Line } from 'react-chartjs-2';
import { Chart, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faTelegram } from '@fortawesome/free-brands-svg-icons';
import { API_URL, CHART_COLORS } from '../config';
import axios from 'axios';

// Register Chart.js components
Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function Dashboard() {
  const [isLoading, setIsLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    userFramework: null,
    userChannels: [],
    chartData: { dates: [], whatsapp: [], telegram: [] },
    todayMessages: 0,
    totalMessages: 0
  });
  const [error, setError] = useState('');
  
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setIsLoading(true);
        const response = await axios.get(`${API_URL}/dashboard/user`);
        setDashboardData(response.data);
      } catch (err) {
        console.error('Error fetching dashboard data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchDashboardData();
  }, []);
  
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    },
    plugins: {
      legend: {
        position: 'top',
      }
    }
  };
  
  const chartConfig = {
    labels: dashboardData.chartData.dates,
    datasets: [
      {
        label: 'WhatsApp',
        data: dashboardData.chartData.whatsapp,
        borderColor: CHART_COLORS.whatsapp,
        backgroundColor: `${CHART_COLORS.whatsapp}20`,
        borderWidth: 2,
        tension: 0.3
      },
      {
        label: 'Telegram',
        data: dashboardData.chartData.telegram,
        borderColor: CHART_COLORS.telegram,
        backgroundColor: `${CHART_COLORS.telegram}20`,
        borderWidth: 2,
        tension: 0.3
      }
    ]
  };
  
  const hasWhatsAppConfigured = dashboardData.userChannels?.some(channel => 
    channel.channel_name === 'WhatsApp');
    
  const hasTelegramConfigured = dashboardData.userChannels?.some(channel => 
    channel.channel_name === 'Telegram');
  
  if (isLoading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading dashboard data...</p>
      </div>
    );
  }
  
  return (
    <Row>
      {/* Configuration Status */}
      <Col md={6} className="mb-4">
        <Card className="h-100 shadow-sm">
          <Card.Header>
            <h5 className="m-0">Configuration Status</h5>
          </Card.Header>
          <Card.Body>
            <div className="mb-4">
              <h6>Framework</h6>
              {dashboardData.userFramework ? (
                <div className="d-flex align-items-center">
                  <Badge bg="success" className="me-2">Configured</Badge>
                  <span>{dashboardData.userFramework.framework_name}</span>
                </div>
              ) : (
                <div className="d-flex align-items-center">
                  <Badge bg="warning" text="dark" className="me-2">Not Configured</Badge>
                  <Link to="/framework">Configure Framework</Link>
                </div>
              )}
            </div>
            
            <div>
              <h6>Channels</h6>
              <div className="d-flex flex-column gap-2">
                {/* WhatsApp Status */}
                <div className="d-flex align-items-center">
                  <Badge bg={hasWhatsAppConfigured ? 'success' : 'warning'} 
                         text={hasWhatsAppConfigured ? 'light' : 'dark'} 
                         className="me-2">
                    {hasWhatsAppConfigured ? 'Configured' : 'Not Configured'}
                  </Badge>
                  <span><FontAwesomeIcon icon={faWhatsapp} className="me-1" /> WhatsApp</span>
                  {!hasWhatsAppConfigured && (
                    <Link to="/channels" className="ms-2">Configure</Link>
                  )}
                </div>
                
                {/* Telegram Status */}
                <div className="d-flex align-items-center">
                  <Badge bg={hasTelegramConfigured ? 'success' : 'warning'} 
                         text={hasTelegramConfigured ? 'light' : 'dark'}
                         className="me-2">
                    {hasTelegramConfigured ? 'Configured' : 'Not Configured'}
                  </Badge>
                  <span><FontAwesomeIcon icon={faTelegram} className="me-1" /> Telegram</span>
                  {!hasTelegramConfigured && (
                    <Link to="/channels" className="ms-2">Configure</Link>
                  )}
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      
      {/* Message Statistics */}
      <Col md={6} className="mb-4">
        <Card className="h-100 shadow-sm">
          <Card.Header>
            <h5 className="m-0">Message Statistics</h5>
          </Card.Header>
          <Card.Body>
            <Row className="mb-4">
              <Col md={6}>
                <Card bg="primary" text="white">
                  <Card.Body>
                    <h6 className="card-title">Today's Messages</h6>
                    <h3 className="mb-0">{dashboardData.todayMessages}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={6}>
                <Card bg="success" text="white">
                  <Card.Body>
                    <h6 className="card-title">Total Messages</h6>
                    <h3 className="mb-0">{dashboardData.totalMessages}</h3>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
            
            <div>
              <h6>Last 7 Days</h6>
              <div style={{ height: 200 }}>
                <Line options={chartOptions} data={chartConfig} />
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
      
      {/* Getting Started Guide */}
      <Col xs={12}>
        <Card className="shadow-sm">
          <Card.Header>
            <h5 className="m-0">Getting Started</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4} className="mb-3">
                <Card className="h-100">
                  <Card.Body>
                    <h5 className="card-title">Step 1: Select Your Framework</h5>
                    <p className="card-text">Choose the e-commerce framework you're using (WordPress/WooCommerce, Shopify, or Odoo).</p>
                    <Link to="/framework" className="btn btn-primary">Configure Framework</Link>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="h-100">
                  <Card.Body>
                    <h5 className="card-title">Step 2: Connect Your Channels</h5>
                    <p className="card-text">Connect WhatsApp and/or Telegram to enable AI responses on these platforms.</p>
                    <Link to="/channels" className="btn btn-primary">Configure Channels</Link>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={4} className="mb-3">
                <Card className="h-100">
                  <Card.Body>
                    <h5 className="card-title">Step 3: Start Chatting</h5>
                    <p className="card-text">Once configured, your customers can start chatting with your AI assistant through the connected channels.</p>
                    <Link to="/messages" className="btn btn-primary">View Messages</Link>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
}

export default Dashboard;
